/**
 * "Who did what, and when" on a quote, RFQ, purchase order, order or cart.
 *
 * Coworkers at the same business domain share these documents, so a buyer
 * opening one needs to see that it was Pat who revised it yesterday and Sam
 * who paid this morning. Entries come from the server's activity trail
 * (functions/src/company-activity.ts); nothing is inferred here.
 */
import { useState } from "react";

export type ActivityActor = {
  uid: string | null;
  email: string;
  name: string;
};

export type ActivityEntry = {
  at: string;
  by: ActivityActor;
  action: string;
  detail: string;
};

/** "Pat Lee" when known, else the address; "you" for the signed-in viewer. */
export const actorLabel = (
  by: ActivityActor | null | undefined,
  viewerEmail?: string | null,
): string => {
  if (!by) return "someone";
  const viewer = (viewerEmail || "").trim().toLowerCase();
  if (viewer && by.email.toLowerCase() === viewer) return "you";
  return by.name || by.email || "someone";
};

/** "2026-08-18, 3:15 PM" in the reader's own timezone. */
export const whenLabel = (iso: string): string =>
  iso
    ? `${iso.slice(0, 10)}, ${new Date(iso).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })}`
    : "";

/** One line: "revised by Pat Lee, 2026-08-18, 3:15 PM". */
export const activityLine = (
  entry: ActivityEntry | null | undefined,
  viewerEmail?: string | null,
): string =>
  entry
    ? `${entry.action} by ${actorLabel(entry.by, viewerEmail)}, ${whenLabel(entry.at)}`
    : "";

/**
 * The trail, newest first, folded behind a "History" link so list rows stay
 * short. `summary` (the latest entry) is always shown when present.
 */
export function ActivityTrail({
  activity,
  viewerEmail,
  className = "",
  startOpen = false,
}: {
  activity: ActivityEntry[] | null | undefined;
  viewerEmail?: string | null;
  className?: string;
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const entries = [...(activity || [])].sort((a, b) =>
    (b.at || "").localeCompare(a.at || ""),
  );
  if (entries.length === 0) return null;
  const latest = entries[0];
  return (
    <div className={`text-xs text-gray-600 ${className}`}>
      <span>
        Last {activityLine(latest, viewerEmail)}
        {latest.detail ? ` (${latest.detail})` : ""}
      </span>
      {entries.length > 1 && (
        <>
          {" "}
          <button
            type="button"
            className="text-blue-700 hover:underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide history" : `History (${entries.length})`}
          </button>
          {open && (
            <ul className="mt-1 space-y-0.5 border-l-2 border-gray-200 pl-3">
              {entries.map((entry, index) => (
                <li key={`${entry.at}-${index}`}>
                  <span className="font-medium text-[#00062e]">
                    {actorLabel(entry.by, viewerEmail)}
                  </span>{" "}
                  {entry.action}
                  {entry.detail ? ` (${entry.detail})` : ""} -{" "}
                  <span className="text-gray-500">{whenLabel(entry.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
